import React, { useState, forwardRef, useImperativeHandle } from "react";
import { CellContext } from "@tanstack/react-table";
import { Interview } from "@/services/interviewService";
import { Star } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface StarRatingCellProps {
  info: CellContext<Interview, unknown>;
  onUpdate: (
    id: string,
    field: keyof Interview,
    value: any,
    skipTranscriptModal?: boolean
  ) => void;
}

export interface StarRatingCellRef {
  openRatingPopup: () => void;
}

export const StarRatingCell = forwardRef<
  StarRatingCellRef,
  StarRatingCellProps
>(({ info, onUpdate }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);
  const currentRating = (info.getValue() as number) || 0;
  const rowIndex = info.row.index;
  const interviewId = info.row.original.id; // Get the interview ID

  useImperativeHandle(ref, () => ({
    openRatingPopup: () => setIsOpen(true),
  }));

  const handleRatingSelect = (rating: number) => {
    // Send null instead of 0 when clearing rating to comply with DB constraint
    const ratingValue = rating === 0 ? null : rating;
    onUpdate(interviewId, "expertRating", ratingValue);
    setIsOpen(false);
  };

  const renderStars = (rating: number, interactive: boolean = false) => {
    return Array.from({ length: 5 }, (_, i) => {
      const starIndex = i + 1;
      const isHovered = interactive && hoveredRating >= starIndex;
      const isFilled = interactive ? isHovered : i < rating;

      return (
        <Star
          key={i}
          className={cn(
            "h-4 w-4",
            interactive &&
              "cursor-pointer hover:scale-110 transition-transform",
            isFilled
              ? "fill-yellow-400 text-yellow-400"
              : "fill-gray-200 text-gray-300"
          )}
          onClick={
            interactive ? () => handleRatingSelect(starIndex) : undefined
          }
          onMouseEnter={
            interactive ? () => setHoveredRating(starIndex) : undefined
          }
          onMouseLeave={interactive ? () => setHoveredRating(0) : undefined}
        />
      );
    });
  };

  return (
    <div className="flex items-center justify-center">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button className="flex items-center space-x-1 p-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            {currentRating > 0 ? (
              renderStars(currentRating)
            ) : (
              <span className="text-gray-400 dark:text-gray-500 text-sm">
                Rate expert
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="center">
          <div className="space-y-2">
            <p className="text-sm font-medium">Rate this expert:</p>
            <div className="flex space-x-1">
              {renderStars(5, true).map((star, i) => (
                <div key={i} className="relative">
                  {star}
                </div>
              ))}
            </div>
            {currentRating > 0 && (
              <button
                onClick={() => handleRatingSelect(0)}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
              >
                Clear rating
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});
